import { JSX } from 'react';
import { Add, Delete } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, IconButton, Typography } from '@mui/material';
import * as Types from 'types/types';

interface ProfileExperienceTabProps {
  experience?: Types.WorkExperience[];
  isMobile: boolean;
  onAddExperience: () => void;
  onRemoveExperience: (index: number) => void;
}

function ProfileExperienceTab({
  experience = [],
  isMobile,
  onAddExperience,
  onRemoveExperience,
}: ProfileExperienceTabProps): JSX.Element {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Work Experience</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={onAddExperience}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Experience
        </Button>
      </Box>

      {experience.map((exp, index) => (
        <Card
          key={`${exp.companyName}-${index}`}
          sx={{ mb: { xs: 1.5, sm: 2 }, overflow: 'hidden' }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  component="div"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}
                >
                  {exp.position}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{ wordBreak: 'break-word', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                >
                  {exp.companyName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {exp.startDate?.toLocaleDateString()} -{' '}
                  {exp.isCurrent ? 'Present' : exp.endDate?.toLocaleDateString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    wordBreak: 'break-word',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  {exp.description}
                </Typography>
                {!!exp.skills?.length && (
                  <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                    {exp.skills.map((skill, skillIndex) => (
                      <Chip
                        key={`${skill}-${skillIndex}`}
                        label={skill}
                        size="small"
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: 20, sm: 24 },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton
                onClick={(): void => onRemoveExperience(index)}
                color="error"
                size="small"
                sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' }, ml: { sm: 1 } }}
              >
                <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}

      {experience.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            py: { xs: 2, sm: 4 },
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
          }}
        >
          No work experience added yet. Click &quot;Add Experience&quot; to get started.
        </Typography>
      )}
    </Box>
  );
}

export { ProfileExperienceTab };
